import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { JSONSchema7 } from "json-schema";
import type { ConnectionProviderKey } from "@forgepoint/shared";

export interface ActionDefinition {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  inputSchema: JSONSchema7;
  outputSchema: JSONSchema7;
  type: "CONDITION" | "ACTION" | "AGENT" | "MESSAGE";
  appConnectionConfig?: { appId: string; providerId: ConnectionProviderKey };
}

interface ActionState {
  isLoadingGet: boolean;
  getError: string | null;
}

interface AgentWorkflowActionsStore {
  actions: Map<string, ActionDefinition>;
  actionState: ActionState;

  fetchActions: () => Promise<void>;
  fetchAppActions: (appId: string) => Promise<void>;
  fetchActionsByIds: (actionIds: string[]) => Promise<void>;
  getActions: () => Map<string, ActionDefinition>;
  getActionById: (actionId: string) => ActionDefinition | undefined;
}

export const useAgentWorkflowActionsStore = create<AgentWorkflowActionsStore>(
  (set, get) => ({
    actions: new Map(),
    actionState: {
      isLoadingGet: false,
      getError: null,
    },

    fetchActions: async () => {
      set({
        actionState: { isLoadingGet: true, getError: null },
      });

      try {
        const response = await fetchApi("apps/actions", "GET");

        if (response.ok && response.data) {
          const data = response.data as { actions: ActionDefinition[] };
          const actionsMap = new Map<string, ActionDefinition>();
          data.actions.forEach((action) => {
            actionsMap.set(action.id, action);
          });
          set({
            actions: actionsMap,
            actionState: { isLoadingGet: false, getError: null },
          });
        } else {
          set({
            actionState: {
              isLoadingGet: false,
              getError: "Failed to fetch actions",
            },
          });
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch actions";
        captureException(error as Error);
        set({
          actionState: { isLoadingGet: false, getError: errorMessage },
        });
      }
    },

    fetchAppActions: async (appId: string) => {
      set({
        actionState: { isLoadingGet: true, getError: null },
      });

      try {
        const response = await fetchApi(`apps/${appId}/actions`, "GET");

        if (response.ok && response.data) {
          const data = response.data as { actions: ActionDefinition[] };
          const currentActions = get().actions;
          const updatedActions = new Map(currentActions);
          data.actions.forEach((action) => {
            updatedActions.set(action.id, action);
          });
          set({
            actions: updatedActions,
            actionState: { isLoadingGet: false, getError: null },
          });
        } else {
          set({
            actionState: {
              isLoadingGet: false,
              getError: "Failed to fetch app actions",
            },
          });
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch app actions";
        captureException(error as Error);
        set({
          actionState: { isLoadingGet: false, getError: errorMessage },
        });
      }
    },

    fetchActionsByIds: async (actionIds: string[]) => {
      if (!actionIds.length) {
        return;
      }

      set({
        actionState: { isLoadingGet: true, getError: null },
      });

      try {
        const response = await fetchApi("apps/actions", "GET", {
          params: { actionIds: actionIds.join(",") },
        });

        if (response.ok && response.data) {
          const data = response.data as { actions: ActionDefinition[] };
          const currentActions = get().actions;
          const updatedActions = new Map(currentActions);
          data.actions.forEach((action) => {
            updatedActions.set(action.id, action);
          });
          set({
            actions: updatedActions,
            actionState: { isLoadingGet: false, getError: null },
          });
        } else {
          set({
            actionState: {
              isLoadingGet: false,
              getError: "Failed to fetch actions by IDs",
            },
          });
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to fetch actions by IDs";
        captureException(error as Error);
        set({
          actionState: { isLoadingGet: false, getError: errorMessage },
        });
      }
    },

    getActions: () => get().actions,

    getActionById: (actionId: string) => get().actions.get(actionId),
  }),
);
